import * as React from "react"
import type { Maybe, Transaction } from "@digits-graphql/frontend/graphql-bearer"
import {
  type BulkUpdate,
  type TransactionSelections,
  type TransactionUpdate,
  type TransactionUpdates,
} from "src/frontend/components/Shared/Transactions/types"

/**
 * INTERFACES
 */

export interface TransactionsUpdatesContextProps {
  // General
  editable: boolean
  editing: boolean
  updating: boolean
  clearUpdates: () => void

  // Inline Updates
  allowInlineUpdates: boolean
  isUpdatingInline: boolean
  transactionUpdates: TransactionUpdates
  getTransactionUpdate: (factId: Maybe<string> | undefined) => TransactionUpdate | undefined
  updateTransaction: (update: TransactionUpdate) => void
  applyTransactionUpdates: () => void

  // Bulk Updates
  allowBulkUpdate: boolean
  isBulkSelecting: boolean
  selectedTransactions: TransactionSelections
  selectTransaction: (t: Transaction) => void
  unselectTransaction: (t: Transaction) => void
  toggleTransaction: (t: Transaction) => void
  applyBulkUpdates: (update: BulkUpdate) => void
}

/**
 * CONTEXT
 */
export const TransactionsUpdatesContext = React.createContext<TransactionsUpdatesContextProps>({
  // editable false by default, so Tables not inside WithTransactionsUpdates wont be editable
  editable: false,
  editing: false,
  updating: false,
  clearUpdates: () => {},

  allowInlineUpdates: false,
  isUpdatingInline: false,
  transactionUpdates: new Map(),
  getTransactionUpdate: () => undefined,
  updateTransaction: () => {},
  applyTransactionUpdates: () => {},

  allowBulkUpdate: false,
  isBulkSelecting: false,
  selectedTransactions: new Map(),
  selectTransaction: () => {},
  unselectTransaction: () => {},
  toggleTransaction: () => {},
  applyBulkUpdates: () => {},
})

export function useTransactionsUpdates() {
  return React.useContext(TransactionsUpdatesContext)
}
